import classNames from 'classnames'

import type { ReactElement } from 'react'
import type { ICluster } from '~/types/cluster'

import TableVariation from '~/components/TableVariation'
import { getNumberWithSpaces } from '~/utils/number'

import './styles.scss'

interface Props {
  className?: string
  metrics: ICluster
}

const ScoreTable = ({ className, metrics }: Props): ReactElement | null => {
  /** Local state */

  const componentClasses = classNames('score-table-component', className)

  const {
    dominance_top5_downloads,
    dominance_top5_revenue,
    downloads_d30,
    downloads_d90,
    downloads_d180,
    downloads_d360,
    revenue_d30,
    revenue_d90,
    revenue_d180,
    revenue_d360,
    sov_downloads_us,
    sov_revenue_us,
  } = metrics

  /** Handlers */

  // This variation is the comparison between the average of xx_days with 360_days average: average_xx_days / average_360_days
  const getCellVariation = (value: number, period: number, type: 'downloads' | 'revenue'): number => {
    if (downloads_d360 && revenue_d360) {
      const y1 = (type === 'downloads' ? downloads_d360 : revenue_d360) / 360
      const y2 = value / period

      return ((y2 - y1) / y1) * 100
    } else {
      return 0
    }
  }

  /** Render */

  return (
    <table className={componentClasses}>
      <thead>
        <tr>
          <th />
          <th>Last 30 Days</th>
          <th>Last 90 Days</th>
          <th>Last 180 Days</th>
          <th>Last 360 days</th>
          <th>Dominance Top Leaders</th>
          <th>
            SOV <span className="flag-country-icon fi fi-us" />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th>Downloads</th>
          <td>
            <div className="variation-wrapper">
              {downloads_d30 !== null ? getNumberWithSpaces(downloads_d30) : '--'}{' '}
              {downloads_d30 ? <TableVariation value={getCellVariation(downloads_d30, 30, 'downloads')} /> : null}
            </div>
          </td>
          <td>
            <div className="variation-wrapper">
              {downloads_d90 !== null ? getNumberWithSpaces(downloads_d90) : '--'}{' '}
              {downloads_d90 ? <TableVariation value={getCellVariation(downloads_d90, 90, 'downloads')} /> : null}
            </div>
          </td>
          <td>
            <div className="variation-wrapper">
              {downloads_d180 !== null ? getNumberWithSpaces(downloads_d180) : '--'}{' '}
              {downloads_d180 ? <TableVariation value={getCellVariation(downloads_d180, 180, 'downloads')} /> : null}
            </div>
          </td>
          <td>{downloads_d360 !== null ? getNumberWithSpaces(downloads_d360) : '--'}</td>
          <td>
            {dominance_top5_downloads !== null
              ? `${getNumberWithSpaces((dominance_top5_downloads * 100).toFixed(2))}%`
              : '--'}
          </td>
          <td>{sov_downloads_us !== null ? `${getNumberWithSpaces((sov_downloads_us * 100).toFixed(2))}%` : '--'}</td>
        </tr>
        <tr>
          <th>Revenue</th>
          <td>
            <div className="variation-wrapper">
              {revenue_d30 !== null ? `${getNumberWithSpaces(revenue_d30)}$` : '--'}{' '}
              {revenue_d30 ? <TableVariation value={getCellVariation(revenue_d30, 30, 'revenue')} /> : null}
            </div>
          </td>
          <td>
            <div className="variation-wrapper">
              {revenue_d90 !== null ? `${getNumberWithSpaces(revenue_d90)}$` : '--'}{' '}
              {revenue_d90 ? <TableVariation value={getCellVariation(revenue_d90, 90, 'revenue')} /> : null}
            </div>
          </td>
          <td>
            <div className="variation-wrapper">
              {revenue_d180 !== null ? `${getNumberWithSpaces(revenue_d180)}$` : '--'}{' '}
              {revenue_d180 ? <TableVariation value={getCellVariation(revenue_d180, 180, 'revenue')} /> : null}
            </div>
          </td>
          <td>{revenue_d360 !== null ? `${getNumberWithSpaces(revenue_d360)}$` : '--'}</td>
          <td>
            {dominance_top5_revenue !== null
              ? `${getNumberWithSpaces((dominance_top5_revenue * 100).toFixed(2))}%`
              : '--'}
          </td>
          <td>{sov_revenue_us !== null ? `${getNumberWithSpaces((sov_revenue_us * 100).toFixed(2))}%` : '--'}</td>
        </tr>
      </tbody>
    </table>
  )
}

export default ScoreTable
